import { memo } from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Icon } from '@liminal/web-ui/icons/Icon';
import type { IconName } from '@liminal/web-ui/icons/registry';
import { brandColors } from '@liminal/brand/palette';
import { borderRadius, fontSizes, spacing, fontWeights } from '@liminal/brand/scale';
import { withOpacity } from '@liminal/brand/color-utils';
import { FONT_STACK_DISPLAY } from '@liminal/brand/font-faces';

/**
 * A scaled render of what the announcement looks like inside the app.
 *
 * The mobile surface is `application/mobile/src/features/announcements/
 * AnnouncementModal.tsx`: a dark overlay, a white card, a tinted icon disc, a
 * centred serif title and body, and an accent-filled action pill. Everything
 * below mirrors that file — the glyph table, the accent per type, the 9% icon
 * tint, the centring. When the modal changes, change this with it; a preview
 * that has drifted is worse than no preview, because it is believed.
 */

const TYPE_CONFIG: Record<string, { icon: IconName; accent: string }> = {
  ANNOUNCEMENT_TYPE_MAINTENANCE: { icon: 'construct', accent: brandColors.orange },
  ANNOUNCEMENT_TYPE_FEATURE: { icon: 'sparkles', accent: brandColors.blue },
  ANNOUNCEMENT_TYPE_URGENT: { icon: 'warning', accent: brandColors.red },
  ANNOUNCEMENT_TYPE_PROMO: { icon: 'gift', accent: brandColors.green },
};

// Mobile falls back to the maintenance styling for a type it does not know,
// so an unknown type previews as the thing users would actually see.
const FALLBACK_CONFIG = TYPE_CONFIG.ANNOUNCEMENT_TYPE_MAINTENANCE;

const Overlay = styled(Box)({
  // Mobile's scrim is 64% black over whatever screen the user was on. A solid
  // dark ground stands in for that: a translucent tint here would composite
  // against the dialog's white paper and read as grey, not as "behind a modal".
  background: `linear-gradient(160deg, ${brandColors.brown}, ${brandColors.black})`,
  borderRadius: borderRadius['2xl'],
  padding: spacing.md,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 360,
});

const Card = styled(Box)({
  position: 'relative',
  width: '100%',
  backgroundColor: brandColors.white,
  borderRadius: borderRadius.xl,
  padding: `${spacing.xl}px ${spacing.md}px ${spacing.lg}px`,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
});

const CloseDisc = styled(Box)({
  position: 'absolute',
  top: spacing.md,
  right: spacing.md,
  width: 28,
  height: 28,
  borderRadius: borderRadius.full,
  backgroundColor: withOpacity(brandColors.black, 0.08),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const IconDisc = styled(Box)({
  width: 64,
  height: 64,
  borderRadius: borderRadius.full,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: spacing.md,
});

const PreviewTitle = styled(Typography)({
  fontFamily: FONT_STACK_DISPLAY,
  fontSize: fontSizes.xl,
  fontWeight: fontWeights.semibold,
  color: brandColors.black,
  textAlign: 'center',
  marginBottom: spacing.sm,
  // Long words in a narrow card would otherwise push the layout wider than
  // the phone it is standing in for.
  overflowWrap: 'anywhere',
});

const PreviewBody = styled(Typography)({
  fontFamily: FONT_STACK_DISPLAY,
  fontSize: fontSizes.sm,
  lineHeight: 1.6,
  color: brandColors.brown,
  textAlign: 'center',
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
});

const ActionPill = styled(Box)({
  marginTop: spacing.md,
  padding: `${spacing.sm}px ${spacing.lg}px`,
  borderRadius: borderRadius.full,
  fontFamily: FONT_STACK_DISPLAY,
  fontSize: fontSizes.xs,
  fontWeight: fontWeights.semibold,
  color: brandColors.white,
  maxWidth: '100%',
  overflowWrap: 'anywhere',
  textAlign: 'center',
});

export interface AnnouncementPreviewProps {
  readonly title: string;
  readonly body: string;
  /** The proto enum, e.g. `ANNOUNCEMENT_TYPE_FEATURE`. */
  readonly type: string;
  readonly actionLabel: string;
  readonly actionUrl: string;
}

export const AnnouncementPreview = memo(function AnnouncementPreview({
  title,
  body,
  type,
  actionLabel,
  actionUrl,
}: AnnouncementPreviewProps) {
  const config = TYPE_CONFIG[type] ?? FALLBACK_CONFIG;
  const trimmedTitle = title.trim();
  const trimmedBody = body.trim();
  // Mobile renders the pill only when both halves are present, so a label
  // typed without a URL must preview as no button at all.
  const showAction = actionLabel.trim() !== '' && actionUrl.trim() !== '';

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Preview
      </Typography>
      <Overlay>
        <Card>
          <CloseDisc>
            <Icon name="close" size={16} color={brandColors.black} />
          </CloseDisc>
          <IconDisc sx={{ backgroundColor: withOpacity(config.accent, 0.09) }}>
            <Icon name={config.icon} size={32} color={config.accent} />
          </IconDisc>
          {/* Placeholders are greyed so an empty field never reads as copy
              that is about to ship. */}
          <PreviewTitle sx={trimmedTitle ? undefined : { color: brandColors.greyLighter }}>
            {trimmedTitle || 'Announcement title'}
          </PreviewTitle>
          <PreviewBody sx={trimmedBody ? undefined : { color: brandColors.greyLighter }}>
            {trimmedBody || 'The announcement body appears here.'}
          </PreviewBody>
          {showAction ? (
            <ActionPill sx={{ backgroundColor: config.accent }}>{actionLabel.trim()}</ActionPill>
          ) : null}
        </Card>
      </Overlay>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Approximate — the app renders this at phone width with the brand fonts.
      </Typography>
    </Box>
  );
});
